import { Show } from "solid-js";
import { useDenominationsGridWebstoreTelescopeContext } from "../context/denominations_grid";
import { useTelescopeContext } from "@hubble/telescope";
import { toRupees } from "~/utils/number";
import { coinIcon } from "~/assets/assets";
import { openModalV2 } from "~/shared_states/modal";
import { CoinsConfirmationModal } from "./coins_confirmation_modal";
import CoinEarningInformation from "./coins_earning_info";
import { HubbleEvent } from "~/data/events";
import { useAnalyticsService } from "~/context/analytics_service_context";

export function GcAmountFooter() {
  const gridState = useDenominationsGridWebstoreTelescopeContext();
  const { components } = useTelescopeContext();
  const analyticsService = useAnalyticsService();
  const { internalState, externalProps, enoughCoinsAvailable } = gridState;
  const {
    selectedAmount,
    rewardDiscountAmount,
    ctaState,
    dryRunErrorMessage,
    voucherCount,
    dueAmount,
    redemptionAmount,
    useRewardCoins,
    isSimulating,
  } = internalState;
  const {
    initiateOrder,
    ctaCopy,
    allowRewardCoins,
    coinsBalance,
    discountPercentage,
    isRewardTypePostPaid,
    offerConstruct,
    brand,
  } = externalProps;

  const isPaymentCtaEnabled = () => {
    return (
      ctaState() === "DEFAULT" &&
      selectedAmount() != null &&
      dryRunErrorMessage() == null &&
      enoughCoinsAvailable()
    );
  };

  const _initiateOrder = () => {
    initiateOrder({
      selectedAmount: internalState.selectedAmount,
      selectedVariantId: internalState.selectedVariantId,
      rewardDiscountAmount: internalState.rewardDiscountAmount,
      dueAmount: internalState.dueAmount,
      redemptionAmount: internalState.redemptionAmount,
      discountDetails: internalState.discountDetails,
      dueAmount2: internalState.dueAmount2,
      discountDetails2: internalState.discountDetails2,
      redemptionAmount2: internalState.redemptionAmount2,
      offerConstructTenure: internalState.offerConstructTenure,
      ctaState: internalState.ctaState,
      isSimulating: internalState.isSimulating,
      useRewardCoins: internalState.useRewardCoins,
      hubbleCoinRewards: internalState.hubbleCoinRewards,
      initialErrorOnSimulate: internalState.initialErrorOnSimulate,
      dryRunErrorMessage: dryRunErrorMessage,
      voucherCount: internalState.voucherCount,
    });
  };

  const showConfirmationModal = () => {
    openModalV2(
      () => (
        <CoinsConfirmationModal
          coinAmount={rewardDiscountAmount()}
          currentBalance={coinsBalance}
          brandLogoUrl={brand.voucherProductMetadata.sqLogoUrl}
          brandTitle={brand.voucherProductMetadata.title}
          onConfirm={_initiateOrder}
        />
      ),
      {
        size: "small",
      }
    );
  };

  const handleCtaClick = () => {
    if (!isPaymentCtaEnabled()) return;

    analyticsService.logEvent(HubbleEvent.CLICK_GET_THIS_CARD, {
      amount: selectedAmount(),
      brandName: brand.name,
    });

    // Show confirmation modal for 0 due amount (coins-only) without offer construct
    if (dueAmount() === 0 && !offerConstruct) {
      showConfirmationModal();
    } else {
      _initiateOrder();
    }
  };

  const getCtaText = () => {
    if (ctaCopy) return ctaCopy;
    return voucherCount() > 1 ? "Get cards" : "Get this card";
  };

  return (
    <div class="flex w-full flex-col">
      {/* Divider */}
      {/* Toggle for reward coins */}
      <Show when={allowRewardCoins && coinsBalance > 0}>
        <div class="h-px w-full bg-stroke-1" />
        <div class="my-4 flex  w-full items-center justify-between">
          <div class="flex flex-1 items-center gap-2">
            <img src={coinIcon} alt="coin" class="h-5 w-5" />
            <div class="flex flex-1 items-center gap-1 text-para-3-regular tracking-[-0.14px]">
              <Show
                when={rewardDiscountAmount() == 0}
                fallback={
                  <>
                    <span class="text-para-3-medium text-text-normal-primary">
                      Using {rewardDiscountAmount()} coins
                    </span>
                  </>
                }
              >
                <span class="text-para-3-medium text-text-normal-primary">
                  Use rewards
                </span>
                <span class="text-text-normal-secondary">·</span>
                <span class="text-para-3-medium text-success-base">
                  Get ₹
                  {externalProps.coinsBalance <=
                  (internalState.dueAmount() ?? 0)
                    ? externalProps.coinsBalance
                    : internalState.dueAmount()}{" "}
                  off
                </span>
              </Show>
            </div>
          </div>
          <components.Toggle
            checked={useRewardCoins() ?? false}
            onChange={(checked) => {
              internalState.setUseRewardCoins(checked);
            }}
          />
        </div>
      </Show>
      <Show when={isRewardTypePostPaid}>
        <div class="h-px w-full bg-stroke-1" />
        <CoinEarningInformation />
      </Show>
      <div class="h-px w-full bg-stroke-1" />
      {/* <Show when={!isRewardTypePostPaid}> */}
      <div class="my-3 flex w-full items-start gap-2">
        <div class="flex flex-1 flex-col">
          <div class="text-para-3-medium tracking-[-0.14px] text-text-normal-primary">
            You pay
          </div>
          <div class="flex items-end gap-[5px]">
            <div
              class="text-title-4-semi-bold tracking-[-0.3px] text-text-normal-primary"
              classList={{
                "!text-success-base":
                  discountPercentage > 0 && !isRewardTypePostPaid,
              }}
            >
              {toRupees(dueAmount() || 0)}
            </div>
            <Show when={discountPercentage > 0 && !isRewardTypePostPaid}>
              <div class="flex h-full items-end">
                <div class="mb-[2px] text-para-3-regular tracking-[-0.14px] text-text-normal-secondary line-through">
                  {toRupees(redemptionAmount() || 0)}
                </div>
              </div>
            </Show>
          </div>
        </div>
        <Show when={discountPercentage > 0 && !isRewardTypePostPaid}>
          <div class="flex h-12 flex-col items-center justify-end py-[2px]">
            <div class="text-para-3-medium tracking-[-0.14px] text-success-base">
              Instant {discountPercentage}% off
            </div>
          </div>
        </Show>
      </div>
      {/* </Show> */}
      {/* Multiple cards info */}
      <Show when={voucherCount() > 1 && dryRunErrorMessage() == null}>
        <div class="mb-3 rounded-lg bg-info-lighter px-4 py-2 text-center text-title-6-medium tracking-[-0.14px] text-info-dark">
          You'll get {voucherCount()} gift cards totalling{" "}
          {toRupees(redemptionAmount() ?? 0)}
        </div>
      </Show>
      {/* CTA Button */}
      <components.Button
        text={getCtaText()}
        variant="primary"
        style="neutral"
        size={40}
        expanded={true}
        disabled={!isPaymentCtaEnabled()}
        loading={ctaState() === "LOADING" || isSimulating()}
        onClick={handleCtaClick}
      />
    </div>
  );
}
